import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Rocket, AlertTriangle, TrendingUp, BookOpen, Target, 
  Shield, DollarSign, BarChart3, ArrowRight, CheckCircle,
  XCircle, Lightbulb, Clock
} from 'lucide-react';

const StartHere = () => {
  return (
    <div className="start-here-page" data-testid="start-here-page">
      {/* Hero Section */}
      <section className="start-hero">
        <motion.div
          className="start-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="hero-icon">
            <Rocket size={48} />
          </div>
          <h1>Start Here</h1>
          <p className="hero-subtitle">
            New to trading? You're in the right place. This guide will help you understand 
            the basics before you begin your learning journey.
          </p>
        </motion.div>
      </section>

      {/* What is Trading Section */}
      <section className="start-section">
        <motion.div
          className="section-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <div className="section-icon">
              <TrendingUp size={28} />
            </div>
            <h2>What is Trading?</h2>
          </div>
          
          <div className="info-card">
            <p>
              <strong>Trading</strong> is the buying and selling of financial assets like stocks, currencies (forex), 
              cryptocurrencies, and commodities with the goal of making a profit from price movements.
            </p>
            
            <div className="concept-grid">
              <div className="concept-item">
                <h4>📈 Stocks</h4>
                <p>Shares of ownership in companies. When you buy a stock, you own a small piece of that company.</p>
              </div>
              <div className="concept-item">
                <h4>💱 Forex</h4>
                <p>Foreign exchange - trading one currency for another. The largest financial market in the world.</p>
              </div>
              <div className="concept-item">
                <h4>🪙 Crypto</h4>
                <p>Digital currencies like Bitcoin and Ethereum. Highly volatile but increasingly popular.</p>
              </div>
              <div className="concept-item">
                <h4>🥇 Commodities</h4>
                <p>Physical goods like gold, silver, oil. Often used to diversify investments.</p>
              </div>
            </div>
            
            <div className="key-point">
              <Lightbulb size={20} />
              <p><strong>Key Concept:</strong> Traders aim to "buy low and sell high" - purchasing assets when prices are low and selling when they rise.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Risks Section */}
      <section className="start-section risks-section">
        <motion.div
          className="section-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <div className="section-icon warning">
              <AlertTriangle size={28} />
            </div>
            <h2>Understanding the Risks</h2>
          </div>
          
          <div className="warning-banner">
            <AlertTriangle size={24} />
            <p>
              <strong>Important:</strong> Trading involves significant risk of financial loss. 
              Most beginner traders lose money. Never invest more than you can afford to lose.
            </p>
          </div>
          
          <div className="risks-grid">
            <div className="risk-item">
              <XCircle size={20} className="risk-icon" />
              <h4>Market Risk</h4>
              <p>Prices can move against you quickly and unpredictably</p>
            </div>
            <div className="risk-item">
              <XCircle size={20} className="risk-icon" />
              <h4>Leverage Risk</h4>
              <p>Borrowing to trade can amplify both gains AND losses</p>
            </div>
            <div className="risk-item">
              <XCircle size={20} className="risk-icon" />
              <h4>Emotional Risk</h4>
              <p>Fear and greed can lead to poor decision-making</p>
            </div>
            <div className="risk-item">
              <XCircle size={20} className="risk-icon" />
              <h4>Scam Risk</h4>
              <p>Many "get rich quick" schemes prey on beginners</p>
            </div>
          </div>
          
          <div className="stat-highlight">
            <p>Studies suggest that <strong>70-90% of retail traders</strong> lose money. This is why education and practice are essential before risking real money.</p>
          </div>
        </motion.div>
      </section>

      {/* Basic Concepts Section */}
      <section className="start-section">
        <motion.div
          className="section-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <div className="section-icon">
              <BookOpen size={28} />
            </div>
            <h2>Basic Market Concepts</h2>
          </div>
          
          <div className="concepts-list">
            <div className="concept-card">
              <h4>Bull vs Bear Markets</h4>
              <p><strong>Bull Market:</strong> Prices are rising, optimism is high</p>
              <p><strong>Bear Market:</strong> Prices are falling, pessimism prevails</p>
            </div>
            
            <div className="concept-card">
              <h4>Support & Resistance</h4>
              <p><strong>Support:</strong> Price level where buying pressure prevents further decline</p>
              <p><strong>Resistance:</strong> Price level where selling pressure prevents further rise</p>
            </div>
            
            <div className="concept-card">
              <h4>Bid & Ask Price</h4>
              <p><strong>Bid:</strong> The price buyers are willing to pay</p>
              <p><strong>Ask:</strong> The price sellers are willing to accept</p>
              <p><strong>Spread:</strong> The difference between bid and ask</p>
            </div>
            
            <div className="concept-card">
              <h4>Volume</h4>
              <p>The number of shares or contracts traded. High volume often confirms price movements.</p>
            </div>
            
            <div className="concept-card">
              <h4>Candlestick Charts</h4>
              <p>Visual representations of price movements showing open, high, low, and close prices for a time period.</p>
            </div>
            
            <div className="concept-card">
              <h4>Risk-Reward Ratio</h4>
              <p>Comparing potential profit to potential loss. A 1:3 ratio means risking £1 to potentially gain £3.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Practice Safely Section */}
      <section className="start-section practice-section">
        <motion.div
          className="section-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <div className="section-icon success">
              <Shield size={28} />
            </div>
            <h2>How to Practice Safely</h2>
          </div>
          
          <p className="section-intro">
            Before risking real money, it's crucial to practice with virtual funds. Here's how to get started safely:
          </p>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Use Demo Accounts</h4>
              <p>Most brokers offer free demo accounts with virtual money. Practice without any financial risk.</p>
              <div className="step-tip">
                <CheckCircle size={16} />
                <span>Our Paper Trading feature uses virtual money!</span>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Start Small</h4>
              <p>When you do start with real money, begin with amounts you can afford to lose completely.</p>
              <div className="step-tip">
                <CheckCircle size={16} />
                <span>Never use rent money, savings, or borrowed funds</span>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Learn First</h4>
              <p>Spend at least 3-6 months learning and practicing before considering real trades.</p>
              <div className="step-tip">
                <CheckCircle size={16} />
                <span>Visit our Learning Hub for free tutorials</span>
              </div>
            </div>
            
            <div className="step-card">
              <div className="step-number">4</div>
              <h4>Keep a Journal</h4>
              <p>Document every trade - why you entered, why you exited, and what you learned.</p>
              <div className="step-tip">
                <CheckCircle size={16} />
                <span>Review your journal weekly to spot patterns</span>
              </div>
            </div>
          </div>
          
          <div className="golden-rules">
            <h3>🏆 Golden Rules for Beginners</h3>
            <ul>
              <li><CheckCircle size={16} /> Never invest money you can't afford to lose</li>
              <li><CheckCircle size={16} /> Don't chase losses - accept them and move on</li>
              <li><CheckCircle size={16} /> Avoid leverage until you're consistently profitable</li>
              <li><CheckCircle size={16} /> Be skeptical of "guaranteed profits" or "secret systems"</li>
              <li><CheckCircle size={16} /> Focus on learning, not earning (especially at the start)</li>
            </ul>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="start-cta">
        <div className="cta-content">
          <h2>Ready to Begin Your Journey?</h2>
          <p>Now that you understand the basics, explore our learning resources and start practicing with virtual money.</p>
          
          <div className="cta-buttons">
            <Link to="/learn" className="btn btn-primary" data-testid="go-to-learning-btn">
              <BookOpen size={18} />
              Go to Learning Hub
            </Link>
            <Link to="/trade" className="btn btn-secondary" data-testid="try-paper-trading-btn">
              <BarChart3 size={18} />
              Try Paper Trading
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .start-here-page {
          min-height: 100vh;
        }

        /* Hero */
        .start-hero {
          padding: 80px 24px 60px;
          text-align: center;
          background: linear-gradient(180deg, rgba(0, 255, 148, 0.05) 0%, transparent 100%);
        }

        .start-hero-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-icon {
          width: 96px;
          height: 96px;
          border-radius: 24px;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
        }

        .start-hero h1 {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          margin-bottom: 16px;
        }

        .hero-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          line-height: 1.7;
        }

        /* Sections */
        .start-section {
          padding: 60px 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 32px;
        }

        .section-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .section-icon.warning {
          background: rgba(255, 200, 50, 0.1);
          color: #ffc832;
        }

        .section-icon.success {
          background: rgba(0, 255, 148, 0.1);
          color: var(--success);
        }

        .section-header h2 {
          font-size: 1.75rem;
          margin: 0;
        }

        /* Info Card */
        .info-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 32px;
        }

        .info-card > p {
          font-size: 1.0625rem;
          color: var(--text-secondary);
          line-height: 1.7;
          margin-bottom: 24px;
        }

        .concept-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .concept-item {
          background: var(--bg-subtle);
          padding: 20px;
          border-radius: 12px;
        }

        .concept-item h4 {
          font-size: 1rem;
          margin-bottom: 8px;
        }

        .concept-item p {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.5;
          margin: 0;
        }

        .key-point {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: rgba(0, 255, 148, 0.08);
          border-radius: 10px;
          color: var(--primary);
        }

        .key-point p {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
        }

        /* Risks Section */
        .risks-section {
          background: var(--bg-subtle);
          max-width: 100%;
          padding: 60px 24px;
        }

        .risks-section .section-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .warning-banner {
          display: flex;
          gap: 16px;
          padding: 20px 24px;
          background: rgba(255, 59, 48, 0.1);
          border: 1px solid rgba(255, 59, 48, 0.3);
          border-radius: 12px;
          margin-bottom: 32px;
          color: var(--error);
        }

        .warning-banner p {
          margin: 0;
          font-size: 14px;
          line-height: 1.6;
          color: var(--text-secondary);
        }

        .risks-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .risk-item {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          padding: 20px;
          border-radius: 12px;
        }

        .risk-icon {
          color: var(--error);
          margin-bottom: 8px;
        }

        .risk-item h4 {
          font-size: 1rem;
          margin-bottom: 8px;
        }

        .risk-item p {
          font-size: 14px;
          color: var(--text-muted);
          margin: 0;
        }

        .stat-highlight {
          text-align: center;
          padding: 24px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
        }

        .stat-highlight p {
          margin: 0;
          font-size: 1rem;
          color: var(--text-secondary);
        }

        .stat-highlight strong {
          color: var(--error);
        }

        /* Concepts List */
        .concepts-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .concept-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          padding: 24px;
          border-radius: 12px;
        }

        .concept-card h4 {
          font-size: 1rem;
          margin-bottom: 12px;
          color: var(--primary);
        }

        .concept-card p {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 8px;
        }

        .concept-card p:last-child {
          margin-bottom: 0;
        }

        /* Practice Section */
        .practice-section {
          background: linear-gradient(180deg, transparent 0%, rgba(0, 255, 148, 0.03) 100%);
        }

        .section-intro {
          font-size: 1.0625rem;
          color: var(--text-secondary);
          margin-bottom: 32px;
          line-height: 1.7;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .step-card {
          background: var(--bg-paper);
          border: 1px solid var(--border);
          padding: 24px;
          border-radius: 12px;
          position: relative;
        }

        .step-number {
          position: absolute;
          top: -12px;
          left: 20px;
          width: 32px;
          height: 32px;
          background: var(--primary);
          color: #0a0a0f;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .step-card h4 {
          margin-top: 8px;
          margin-bottom: 12px;
          font-size: 1rem;
        }

        .step-card > p {
          font-size: 14px;
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 16px;
        }

        .step-tip {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: rgba(0, 255, 148, 0.08);
          border-radius: 8px;
          font-size: 13px;
          color: var(--primary);
        }

        .golden-rules {
          background: var(--bg-paper);
          border: 2px solid var(--primary);
          border-radius: 16px;
          padding: 32px;
        }

        .golden-rules h3 {
          font-size: 1.25rem;
          margin-bottom: 20px;
          text-align: center;
        }

        .golden-rules ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .golden-rules li {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 15px;
          color: var(--text-secondary);
        }

        .golden-rules li svg {
          color: var(--primary);
          flex-shrink: 0;
        }

        /* CTA Section */
        .start-cta {
          padding: 80px 24px;
          text-align: center;
          background: var(--bg-subtle);
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .start-cta h2 {
          font-size: 2rem;
          margin-bottom: 16px;
        }

        .start-cta > .cta-content > p {
          color: var(--text-muted);
          margin-bottom: 32px;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-buttons .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary);
          color: #0a0a0f;
          border: none;
        }

        .btn-primary:hover {
          background: var(--primary-light);
        }

        .btn-secondary {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-secondary:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        @media (max-width: 768px) {
          .concept-grid,
          .risks-grid,
          .concepts-list,
          .steps-grid {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-buttons .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default StartHere;
